"use client";

import FormattedDate from "@/components/components/FormattedDate";
import type { Idea, Tag } from "@/types/thinknado";
import clsx from "clsx";
import { Eye, Hash, Trash, X } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import If from "@/components/common/If";
import { useChallenges } from "@/providers/Challenges";

type IdeaWithTags = Idea & {
  tags?: Tag[];
  _count?: {
    bets: number;
  };
};

export default function IdeaCard({ idea }: { idea: IdeaWithTags }) {
  const [showTags, setShowTags] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [tags, setTags] = useState<Tag[]>(idea?.tags || []);
  const { setIdeas } = useChallenges();

  const handleDelete = async () => {
    if (!confirm("Delete this idea? This cannot be undone.")) {
      return;
    }
    const response = await fetch(`/app/api/ideas/${idea.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      alert("Error deleting idea. Please try again.");
      return;
    }

    setIdeas((prev) => prev.filter((item) => item.id !== idea.id));
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const response = await fetch(`/app/api/ideas/${idea.id}`, {
        method: "POST",
        body: JSON.stringify({ tag: newTag }),
      });

      if (response.ok) {
        const newTags = await response.json();
        setShowTags(false);
        setNewTag("");
        setTags([...newTags.newTags]);
        setTimeout(() => {
          console.log("updating tags", tags);
        }, 100);
      }
    }
  };

  const removeTag = async (id: string) => {
    const response = await fetch(`/app/api/ideas/${idea.id}`, {
      method: "PUT",
      body: JSON.stringify({ tag: id }),
    });

    if (response.ok) {
      console.log("tag removed");
      setTags(tags.filter((tag) => tag.id !== id));
    }
  };

  return (
    <>
      <article
        className={clsx(
          "flex flex-col justify-between gap-2 p-4 max-w-full aspect-square",
          "bg-[#cf4c67] text-white challenge-card aspect-square relative"
        )}
      >
        <Link
          href={`/app/session/portfolio/${idea.id}`}
          className="absolute inset-0 z-0"
          aria-label={`View idea: ${idea.idea}`}
        >
          <span className="sr-only">View idea details</span>
        </Link>
        
        <header className="stack gap-2 relative z-10">
          <div className="flex items-center gap-2">
            <button
              className="p-0 flex items-center gap-2 add-tags"
              onClick={() => {
                setShowTags(true);
              }}
              key={`tags-${tags.length}`}
              aria-label={tags?.length > 0 ? "Manage tags" : "Add tags"}
            >
              <Hash />{" "}
              {tags?.length > 0 ? (
                tags.map((tag) => (
                  <button
                    key={tag.id}
                    className="tag flex items-center gap-1"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      removeTag(tag.id);
                    }}
                    aria-label={`Remove tag: ${tag.name}`}
                  >
                    {tag.name}
                    <X size={16} className="relative top-[2px]" />
                  </button>
                ))
              ) : (
                <span>Add Tags</span>
              )}
            </button>
          </div>
          <h2 className={`text-xl font-bold`}>{idea.idea}</h2>
        </header>
        <div className="stack gap-8">
        <div className="flex items-end gap-2 relative z-10">
          <div className="flex items-center gap-8">
              <span className="text-sm text-current flex items-center gap-1 text-white font-light">
                Bets {idea._count?.bets || 0}
              </span>
            </div>
          </div>
          <div className="px-0 pb-4 flex justify-between items-center">
          <FormattedDate
            className={`opacity-50 text-white`}
            date={idea.createdAt?.toString() || ""}
          />
          <div className="flex items-center gap-2">

            <Link 
              href={idea.thinknadoId ? `/app/session/${idea.thinknadoId}` : "/app/session"}
              aria-label="View source session"
            >
              <Eye color="white" />
            </Link>
            <button
              type="button"
              onClick={handleDelete}
              aria-label="Delete idea"
              className="text-white/80 hover:text-white transition-colors"
            >
              <Trash size={18} />
            </button>
          </div>
        </div>
        </div>
      </article>
      <If condition={showTags}>
        <div className="fixed top-0 left-0 w-full h-full grid place-items-center">
          <div
            className="absolute top-0 left-0 w-full h-full bg-[#ffffff80] cursor-pointer"
            onClick={() => setShowTags(false)}
          ></div>
          <input
            type="text"
            onKeyDown={handleKeyDown}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            className="max-w-full narrow-container h-[80px] text-[2rem] bg-white p-2 rounded-thinknado relative z-10 border-2 border-pink px-4"
            autoFocus
          />
        </div>
      </If>
    </>
  );
}
